import { AddExceptionDialog } from '@/components/call-routing/add-exception-dialog'
import { AddTargetDialog } from '@/components/call-routing/add-target-dialog'
import { DeleteExceptionDialog } from '@/components/call-routing/delete-exception-dialog'
import { DeleteTargetDialog } from '@/components/call-routing/delete-target-dialog'
import { ExceptionCard } from '@/components/call-routing/exception-card'
import { RoutingList } from '@/components/call-routing/routing-list'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCallRoutingStore } from '@/stores/call-routing-store'
import { useDevicesStore } from '@/stores/devices-store'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function MdsConfigurationPage() {
    const { t } = useTranslation()
    const { currentDevice, isMdsDevice } = useDevicesStore()
    const [activeTab, setActiveTab] = useState<'routing' | 'exception'>('routing')
    const [selectedExceptionIndex, setSelectedExceptionIndex] = useState<number>(0)
    const [showAddExceptionDialog, setShowAddExceptionDialog] = useState(false)
    const [showDeleteExceptionDialog, setShowDeleteExceptionDialog] = useState(false)
    const [showAddTargetDialog, setShowAddTargetDialog] = useState(false)
    const [showDeleteTargetDialog, setShowDeleteTargetDialog] = useState(false)
    const [currentTargetNumber, setCurrentTargetNumber] = useState('')

    // Subscribe to store state for reactivity
    const callRoutingConfig = useCallRoutingStore((state) => state.callRoutingConfigurationObject)
    const initCallRoutingConfiguration = useCallRoutingStore((state) => state.initCallRoutingConfiguration)
    const addNewCallRoutingObject = useCallRoutingStore((state) => state.addNewCallRoutingObject)
    const deleteCallRoutingException = useCallRoutingStore((state) => state.deleteCallRoutingException)
    const addExceptionCallRoutingTarget = useCallRoutingStore((state) => state.addExceptionCallRoutingTarget)
    const deleteExceptionCallRoutingTarget = useCallRoutingStore((state) => state.deleteExceptionCallRoutingTarget)

    useEffect(() => {
        initCallRoutingConfiguration()
    }, [initCallRoutingConfiguration])

    // Derive exception list from subscribed state (reactive)
    const exceptionList = useMemo(() => {
        if (!callRoutingConfig?.selectionElements) return []
        return callRoutingConfig.selectionElements.filter(el => !el.defaultElement)
    }, [callRoutingConfig])

    const selectedExceptionObject = useMemo(() => {
        if (selectedExceptionIndex >= 0 && selectedExceptionIndex < exceptionList.length) {
            return exceptionList[selectedExceptionIndex]
        }
        return null
    }, [exceptionList, selectedExceptionIndex])

    if (!currentDevice) {
        return (
            <div className="flex items-center justify-center h-screen">
                <p className="text-gray-600">{t('mdsConfig.noDevice')}</p>
            </div>
        )
    }

    if (!isMdsDevice()) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <p className="text-gray-600 font-medium">{t('mdsConfig.notMds')}</p>
                    <p className="text-sm text-gray-500 mt-2">
                        {t('mdsConfig.notMdsHint')}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto max-w-6xl py-6 px-6">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'routing' | 'exception')}>
                    <TabsList className="mb-6 bg-white shadow-sm">
                        <TabsTrigger value="routing" className="px-6 py-2.5">
                            {t('mdsConfig.routing')}
                        </TabsTrigger>
                        <TabsTrigger value="exception" className="px-6 py-2.5">
                            {t('mdsConfig.exception')}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="routing" className="mt-0">
                        <RoutingList />
                    </TabsContent>

                    <TabsContent value="exception" className="mt-0">
                        <div className="space-y-4">
                            {exceptionList.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2 bg-white p-4 rounded-lg shadow-sm">
                                    {exceptionList.map((_: any, index: number) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedExceptionIndex(index)}
                                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${selectedExceptionIndex === index
                                                ? 'bg-brand-primary text-white'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                        >
                                            {t('mdsConfig.exceptionLabel', { index: index + 1 })}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setShowAddExceptionDialog(true)}
                                        className="px-4 py-2 text-sm font-medium rounded-md bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors"
                                    >
                                        <Plus className="h-4 w-4 inline mr-1" />
                                        {t('mdsConfig.new')}
                                    </button>
                                </div>
                            )}

                            {exceptionList.length === 0 && (
                                <div className="bg-white rounded-lg p-12 text-center shadow-sm">
                                    <div className="flex flex-col items-center gap-4">
                                        <h3 className="text-lg font-medium text-gray-700">
                                            {t('mdsConfig.noExceptions')}
                                        </h3>
                                        <p className="text-sm text-gray-500 max-w-md">
                                            {t('mdsConfig.noExceptionsHint')}
                                        </p>
                                        <button
                                            onClick={() => setShowAddExceptionDialog(true)}
                                            className="mt-2 bg-white p-3 rounded-full border border-gray-100 hover:bg-gray-50"
                                        >
                                            <Plus className="text-brand-primary h-5 w-5" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {selectedExceptionObject && (
                                <ExceptionCard
                                    exceptionObject={selectedExceptionObject}
                                    onUpdateTime={() => { }}
                                    onUpdateGreeting={() => { }}
                                    onUpdateGoodbye={() => { }}
                                    onUpdateTarget={() => { }}
                                    onDeleteTarget={(_targetIndex: number, targetNumber: string) => {
                                        setCurrentTargetNumber(targetNumber)
                                        setShowDeleteTargetDialog(true)
                                    }}
                                    onAddTarget={() => setShowAddTargetDialog(true)}
                                    onDeleteException={() => setShowDeleteExceptionDialog(true)}
                                />
                            )}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            <AddExceptionDialog
                isOpen={showAddExceptionDialog}
                onClose={() => setShowAddExceptionDialog(false)}
                onAdd={async (exceptionData) => {
                    await addNewCallRoutingObject({
                        ...exceptionData,
                        defaultElement: false
                    })
                    await initCallRoutingConfiguration()
                    setShowAddExceptionDialog(false)
                    // Select the newly added exception
                    setSelectedExceptionIndex(exceptionList.length)
                }}
            />

            <DeleteExceptionDialog
                isOpen={showDeleteExceptionDialog}
                onClose={() => setShowDeleteExceptionDialog(false)}
                onDelete={async () => {
                    await deleteCallRoutingException(selectedExceptionIndex)
                    await initCallRoutingConfiguration()
                    setShowDeleteExceptionDialog(false)
                    // Select first exception or reset
                    if (exceptionList.length > 1) {
                        setSelectedExceptionIndex(0)
                    } else {
                        setSelectedExceptionIndex(0)
                    }
                }}
                exceptionIndex={selectedExceptionIndex}
            />

            <AddTargetDialog
                isOpen={showAddTargetDialog}
                onClose={() => setShowAddTargetDialog(false)}
                onAdd={async (targetData: any) => {
                    await addExceptionCallRoutingTarget(selectedExceptionIndex, targetData)
                    await initCallRoutingConfiguration()
                    setShowAddTargetDialog(false)
                }}
                isException={true}
            />

            <DeleteTargetDialog
                isOpen={showDeleteTargetDialog}
                onClose={() => setShowDeleteTargetDialog(false)}
                onDelete={async () => {
                    await deleteExceptionCallRoutingTarget(selectedExceptionIndex, currentTargetNumber)
                    await initCallRoutingConfiguration()
                    setShowDeleteTargetDialog(false)
                }}
                targetNumber={currentTargetNumber}
            />
        </div>
    )
}
